import './tools.css';

// In-page dialogs for the annotation tools. Browser dialogs (prompt/alert/confirm) are never used:
// they block the page, cannot be styled and are suppressed in some embeddings.

let host: HTMLElement | null = null;

/** Where dialogs are mounted (the shell's overlay layer); document.body until set. */
export function setDialogHost(element: HTMLElement): void {
  host = element;
}

interface DialogParts {
  backdrop: HTMLDivElement;
  form: HTMLFormElement;
  body: HTMLDivElement;
}

function buildDialog(title: string, confirmLabel: string, className: string): DialogParts {
  const backdrop = document.createElement('div');
  backdrop.className = 'annot-dialog-backdrop';
  const form = document.createElement('form');
  form.className = `annot-dialog ${className}`;
  form.setAttribute('role', 'dialog');
  form.setAttribute('aria-modal', 'true');
  form.setAttribute('aria-label', title);
  const heading = document.createElement('h2');
  heading.className = 'annot-dialog-title';
  heading.textContent = title;
  const body = document.createElement('div');
  body.className = 'annot-dialog-body';
  const actions = document.createElement('div');
  actions.className = 'annot-dialog-actions';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'annot-dialog-cancel';
  cancel.textContent = 'Cancelar';
  const confirm = document.createElement('button');
  confirm.type = 'submit';
  confirm.className = 'annot-dialog-confirm';
  confirm.textContent = confirmLabel;
  actions.append(cancel, confirm);
  form.append(heading, body, actions);
  backdrop.appendChild(form);
  return { backdrop, form, body };
}

/**
 * Shows a dialog and resolves with `read()` on confirm or null on cancel / Escape / backdrop click.
 * Key presses stay inside the dialog so viewer shortcuts (L, A, E…) do not fire while typing.
 */
function run<T>(parts: DialogParts, focus: HTMLElement, read: () => T): Promise<T | null> {
  return new Promise((resolve) => {
    const { backdrop, form } = parts;
    const close = (value: T | null) => {
      backdrop.remove();
      resolve(value);
    };
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      close(read());
    });
    form.querySelector('.annot-dialog-cancel')?.addEventListener('click', () => close(null));
    backdrop.addEventListener('mousedown', (event) => {
      if (event.target === backdrop) close(null);
    });
    backdrop.addEventListener('keydown', (event) => {
      event.stopPropagation();
      if (event.key === 'Escape') close(null);
    });
    backdrop.addEventListener('keyup', (event) => event.stopPropagation());
    (host ?? document.body).appendChild(backdrop);
    focus.focus();
  });
}

/** Asks for an annotation text; resolves with the trimmed text ('' when left empty) or null when cancelled. */
export function promptText(message: string, initial = ''): Promise<string | null> {
  const parts = buildDialog('Texto', 'Aceptar', 'annot-text-dialog');
  const label = document.createElement('label');
  label.className = 'annot-dialog-label';
  label.textContent = message;
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'annot-dialog-input';
  input.value = initial;
  input.maxLength = 200;
  label.appendChild(input);
  parts.body.appendChild(label);
  input.select();
  return run(parts, input, () => input.value.trim());
}

/** "Marcación de Columna": pick the first spine label. */
export function chooseSpineLabel(labels: readonly string[], initial: string): Promise<string | null> {
  const parts = buildDialog('Marcación de Columna', 'Iniciar', 'annot-spine-dialog');
  const label = document.createElement('label');
  label.className = 'annot-dialog-label';
  label.textContent = 'Escoja la etiqueta con la cual iniciar la marcación:';
  const select = document.createElement('select');
  select.className = 'annot-dialog-select';
  for (const value of labels) select.add(new Option(value, value, value === initial, value === initial));
  label.appendChild(select);
  parts.body.appendChild(label);
  return run(parts, select, () => select.value);
}
